// src/firebase/sessionService.js - Updated with user authentication
import { 
  collection, 
  addDoc, 
  doc, 
  query, 
  where, 
  getDocs, 
  deleteDoc,
  Timestamp 
} from 'firebase/firestore';
import { 
  ref, 
  uploadBytes, 
  getDownloadURL, 
} from 'firebase/storage';
import { db, storage } from './config';
import { getCurrentUser, canSaveSession, incrementSessionCount } from '../components/services/authService';
import { resolveVideoUploadType } from '../utils/videoFileTypes';

// Save session to Firebase
export const saveSession = async (videoFile, segments, settings, filename) => {
  try {
    // Check authentication
    const currentUser = getCurrentUser();
    if (!currentUser) {
      throw new Error('You must be signed in to save videos. Please sign in to continue.');
    }

    // Check if user can save (has remaining sessions or is premium)
    const canSave = await canSaveSession(currentUser.uid);
    if (!canSave) {
      throw new Error('You have reached your free-tier video limit. Please free up space or upgrade for more storage.');
    }

    console.log('🟡 Starting session save with video upload...');
    
    const sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    // Upload video to Storage with user-specific path
    console.log('🟡 Uploading video to Storage...');
    const { contentType, extension } = await resolveVideoUploadType(videoFile);
    const videoPath = `users/${currentUser.uid}/videos/${sessionId}.${extension}`;
    const videoRef = ref(storage, videoPath);
    const videoSnapshot = await uploadBytes(videoRef, videoFile, { contentType });
    const videoUrl = await getDownloadURL(videoSnapshot.ref);
    console.log('🟢 Video upload successful!');
    
    // Calculate expiration date (30 days from now)
    const expirationDate = new Date();
    expirationDate.setDate(expirationDate.getDate() + 30);
    
    // Save session data to Firestore
    const sessionData = {
      sessionId,
      userId: currentUser.uid,
      videoUrl,
      videoPath,
      segments,
      settings,
      filename,
      createdAt: Timestamp.now(),
      expiresAt: Timestamp.fromDate(expirationDate)
    };
    
    console.log('🟡 Saving to Firestore with video URL...');
    await addDoc(collection(db, 'sessions'), sessionData);
    
    // Increment user's session count
    await incrementSessionCount(currentUser.uid);
    
    console.log('🟢 Complete save successful!');
    
    return {
      sessionId,
      shareUrl: `${window.location.origin}/?session=${sessionId}`,
      expiresAt: expirationDate
    };
    
  } catch (error) {
    console.error('🔴 Error saving session:', error);
    throw new Error('Failed to save session: ' + (error instanceof Error ? error.message : 'Unknown error'));
  }
};

// Load session from Firebase
export const loadSession = async (sessionId) => {
  try {
    // Query Firestore for the session
    const sessionsRef = collection(db, 'sessions');
    const q = query(sessionsRef, where('sessionId', '==', sessionId));
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      throw new Error('Session not found');
    }
    
    const sessionDoc = querySnapshot.docs[0];
    const sessionData = sessionDoc.data();
    
    // Check if session is expired
    const now = new Date();
    const expirationDate = sessionData.expiresAt.toDate();
    
    if (expirationDate < now) {
      // Clean up expired session
      await cleanupSession(sessionDoc.id, sessionData.videoPath);
      throw new Error('Session has expired');
    }
    
    return {
      ...sessionData,
      expiresAt: expirationDate,
      createdAt: sessionData.createdAt.toDate()
    };
    
  } catch (error) {
    console.error('Error loading session:', error);
    throw new Error('Failed to load session: ' + error.message);
  }
};

// Clean up expired session
const cleanupSession = async (docId, videoPath) => {
  try {
    // Delete from Firestore
    await deleteDoc(doc(db, 'sessions', docId));
    
    
    console.log('Cleaned up expired session metadata (original video retained)');
  } catch (error) {
    console.error('Error cleaning up session:', error);
  }
};

// Manual cleanup function (you can call this periodically)
export const cleanupExpiredSessions = async () => {
  try {
    const now = Timestamp.now();
    const sessionsRef = collection(db, 'sessions');
    const q = query(sessionsRef, where('expiresAt', '<', now));
    const querySnapshot = await getDocs(q);
    
    const cleanupPromises = querySnapshot.docs.map(sessionDoc => {
      const sessionData = sessionDoc.data();
      return cleanupSession(sessionDoc.id, sessionData.videoPath);
    });
    
    await Promise.all(cleanupPromises);
    console.log(`Cleaned up ${querySnapshot.docs.length} expired sessions`);
    
  } catch (error) {
    console.error('Error during cleanup:', error);
  }
};
